import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, AlertTriangle, Loader2, Save, Users, PlayCircle, Check, X, Trash2, Info } from "lucide-react";

const CAMPI = [
  ["cartella_template", "ID cartella template", "DRIVE RB / CONTRATTI / TEMPLATE GENERATORE"],
  ["versione_attiva", "Versione template attiva", "Nome della sottocartella da cui leggere i 4 file JSON"],
  ["cartella_clienti", "ID cartella clienti persone", "DRIVE RB / CLIENTI"],
  ["cartella_clienti_impresa", "ID cartella clienti impresa", "DRIVE RB / CLIENTI BUSINESS"],
  ["cartella_archivio", "ID cartella archivio", "DRIVE RB / CONTRATTI / CONTRATTI STAMPATI"],
];

export default function DocumentiImpostazioni() {
  const [conf, setConf] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [prova, setProva] = useState(null);
  const [pulisco, setPulisco] = useState(false);

  const { data: confDb, refetch: refetchConf } = useQuery({
    queryKey: ["impostazioni-documenti"],
    queryFn: async () => (await api.get("/impostazioni/documenti")).data,
  });
  const { data: legacy, isLoading, error, refetch: refetchLegacy } = useQuery({
    queryKey: ["documenti-tipi-legacy"],
    queryFn: async () => (await api.get("/admin/tipi-legacy")).data,
  });

  const { data: elencoTipi } = useQuery({
    queryKey: ["tipi-pratica-catalogo"],
    queryFn: async () => (await api.get("/documenti/tipi-pratica-catalogo")).data,
  });

  const { data: verifica, refetch: refetchVerifica } = useQuery({
    queryKey: ["verifica-motore"],
    queryFn: async () => (await api.get("/documenti/verifica-motore")).data,
    refetchInterval: (q) => (q?.state?.data?.in_corso ? 3000 : false),
  });

  useEffect(() => { if (confDb) setConf(confDb); }, [confDb]);

  const avviaVerifica = async () => {
    try {
      await api.post("/documenti/verifica-motore");
      toast.success("Verifica avviata: ci vogliono un paio di minuti");
      refetchVerifica();
    } catch (e) { toast.error(e?.response?.data?.detail || "Avvio non riuscito"); }
  };

  const elencaProva = async () => {
    setPulisco(true);
    try {
      const { data } = await api.get("/admin/dati-prova");
      setProva(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Elenco non riuscito"); }
    finally { setPulisco(false); }
  };

  const eliminaProva = async () => {
    setPulisco(true);
    try {
      const { data } = await api.post("/admin/dati-prova/elimina", { conferma: true });
      toast.success(`Rimossi: ${data.clienti} clienti, ${data.pratiche} pratiche, ${data.registro} documenti · ${data.cestinati_su_drive.length} voci nel cestino di Drive`);
      setProva(null);
      refetchLegacy();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pulizia non riuscita"); }
    finally { setPulisco(false); }
  };

  const salva = async () => {
    setSalvo(true);
    try {
      await api.put("/impostazioni/documenti", conf);
      toast.success("Impostazioni documenti salvate");
      refetchConf(); refetchLegacy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSalvo(false); }
  };

  const cambiaSelezionabile = async (t) => {
    try {
      await api.post(`/tipi-pratica/${t.id}/selezionabile`, { selezionabile: t.non_selezionabile });
      toast.success(t.non_selezionabile
        ? `«${t.nome}» torna selezionabile per le pratiche nuove`
        : `«${t.nome}» non è più selezionabile per le pratiche nuove`);
      refetchLegacy();
    } catch { toast.error("Non riuscito"); }
  };

  return (
    <div data-testid="documenti-impostazioni" className="bento bg-white border border-[#E2E8F0] p-6">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#C9A84C]" />
        <h2 className="text-base md:text-lg font-semibold text-[#1A2B4A]">Documenti</h2>
      </div>
      <p className="text-[12px] text-[#4a5568] mt-1">
        I testi dei contratti stanno nei quattro file JSON su Drive, dentro la sottocartella della versione
        attiva. In uso c'è solo la versione scritta qui sotto: caricare una cartella nuova non cambia nulla
        finché non aggiorni il campo, così una bozza non va in uso per sbaglio.
      </p>

      {conf && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {CAMPI.map(([k, label, aiuto]) => (
            <label key={k} className="block">
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">{label}</span>
              <input data-testid={`conf-doc-${k}`} value={conf[k] || ""}
                onChange={(e) => setConf({ ...conf, [k]: e.target.value })}
                className="mt-1 w-full text-[12px] px-2 py-1.5 rounded-md border border-[#1A2B4A]/15 bg-[#f9f8f5] text-[#1A2B4A] focus:border-[#C9A84C] outline-none" />
              <span className="text-[10px] text-[#8a93a6]">{aiuto}</span>
            </label>
          ))}
          <div className="sm:col-span-2 flex justify-end">
            <button data-testid="conf-doc-salva" onClick={salva} disabled={salvo}
              className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-md hover:bg-[#22345c] disabled:opacity-50">
              {salvo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salva
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="mt-4 text-sm text-[#4a5568] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Leggo il catalogo da Drive…
        </p>
      )}
      {error && (
        <p data-testid="documenti-errore" className="mt-4 text-sm text-[#9b2c2c]">
          {error?.response?.data?.detail || "Catalogo template non raggiungibile"}
        </p>
      )}


      {/* Verifica motore */}
      <div className="mt-5 pt-4 border-t border-[#1A2B4A]/10">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[12px] font-semibold text-[#1A2B4A]">Verifica motore</h3>
          <span className="text-[11px] text-[#8a93a6]">
            Controlla ambiente, template e i 4 casi di collaudo. Non salva niente su Drive né nel registro.
          </span>
          <button data-testid="verifica-motore-avvia" onClick={avviaVerifica} disabled={verifica?.in_corso}
            className="ml-auto inline-flex items-center gap-1.5 text-xs border border-[#1A2B4A]/25 px-3 py-1.5 rounded-md hover:border-[#C9A84C] disabled:opacity-50">
            {verifica?.in_corso ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            {verifica?.in_corso ? "Verifica in corso…" : "Verifica motore"}
          </button>
        </div>

        {verifica?.righe?.length > 0 && (
          <div data-testid="verifica-motore-esito" className="mt-3 rounded-md border border-[#E2E8F0] overflow-hidden">
            <div className={`px-3 py-2 flex flex-wrap items-center gap-2 text-[12px] ${verifica.ok ? "bg-[#E7F2EC] text-[#1F5C42]" : "bg-[#FDECEC] text-[#9b2c2c]"}`}>
              {verifica.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              <strong data-testid="verifica-motore-stato">{verifica.ok ? "Tutto in ordine" : "Ci sono punti da vedere"}</strong>
              <span>· versione {verifica.versione}</span>
              {verifica.confronto_attesi === false && <span>· pagine mostrate senza confronto con i PDF attesi</span>}
              <span className="ml-auto text-[11px]">
                {verifica.quando && new Date(verifica.quando).toLocaleString("it-IT")}
              </span>
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                {verifica.righe.map((r) => (
                  <tr key={r.voce} data-testid={`verifica-riga-${r.voce}`} className="border-t border-[#E2E8F0]">
                    <td className="px-3 py-1.5 w-6">
                      {r.esito ? <Check className="w-3.5 h-3.5 text-[#1F5C42]" /> : <X className="w-3.5 h-3.5 text-[#9b2c2c]" />}
                    </td>
                    <td className="px-1 py-1.5 font-medium text-[#1A2B4A] whitespace-nowrap align-top">{r.voce}</td>
                    <td className="px-3 py-1.5 text-[#4a5568] break-words">{r.dettaglio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tutti i tipi pratica del database */}
      {elencoTipi && (
        <div className="mt-5 pt-4 border-t border-[#1A2B4A]/10">
          <h3 className="text-[12px] font-semibold text-[#1A2B4A]">Tipi pratica e catalogo template</h3>
          <p className="text-[11px] text-[#8a93a6] mt-0.5">
            <span data-testid="tipi-abbinamento">
              Abbinamento <strong>per {elencoTipi.abbinamento}</strong>: {elencoTipi.voci_con_codice} voci
              del catalogo {elencoTipi.versione_template} su {elencoTipi.voci_catalogo} hanno il campo «codice».
            </span>{" "}
            Tutti i tipi del database con la chiave del catalogo corrispondente.
            Nessun abbinamento automatico: dove c'è «nessuna», «Genera documento» mostra solo i documenti per il cliente.
            Rinominare un tipo non tocca le pratiche collegate, ma per far combaciare il catalogo il nome deve
            essere identico alla chiave.
          </p>
          <div className="mt-2 max-h-72 overflow-auto rounded-md border border-[#E2E8F0]">
            <table data-testid="tipi-catalogo-tabella" className="w-full text-[11px]">
              <thead className="bg-[#f9f8f5] text-[#8a93a6] sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">Codice</th>
                  <th className="text-left px-2 py-1.5 font-medium">Nome esatto</th>
                  <th className="text-right px-2 py-1.5 font-medium">Pratiche</th>
                  <th className="text-left px-2 py-1.5 font-medium">Attivo</th>
                  <th className="text-left px-2 py-1.5 font-medium">Chiave del catalogo</th>
                </tr>
              </thead>
              <tbody>
                {elencoTipi.tipi.map((t) => (
                  <tr key={t.id} data-testid={`tipo-catalogo-${t.codice || t.id}`} className="border-t border-[#E2E8F0]">
                    <td className="px-2 py-1.5 text-[#8a93a6] whitespace-nowrap">{t.codice || "—"}</td>
                    <td className="px-2 py-1.5 text-[#1A2B4A]">{t.nome}</td>
                    <td className="px-2 py-1.5 text-right text-[#4a5568]">{t.n_pratiche}</td>
                    <td className="px-2 py-1.5">{t.attivo ? "sì" : "no"}{t.non_selezionabile ? " · non selezionabile" : ""}</td>
                    <td className={`px-2 py-1.5 ${t.chiave_catalogo ? "text-[#1F5C42]" : "text-[#9b2c2c]"}`}>
                      {t.chiave_catalogo || "nessuna"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Come si aggiorna un template */}
      <div className="mt-5 pt-4 border-t border-[#1A2B4A]/10">
        <h3 className="text-[12px] font-semibold text-[#1A2B4A] flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-[#C9A84C]" /> Come si aggiorna un template
        </h3>
        <ol data-testid="nota-aggiorna-template" className="mt-1.5 space-y-1 text-[11px] text-[#4a5568] list-decimal pl-4">
          <li>Su Drive, dentro DRIVE RB / CONTRATTI / TEMPLATE GENERATORE, crea una nuova sottocartella (per esempio v1.2) con i 4 file JSON.</li>
          <li>Cambia qui sopra «Versione template attiva», salva e premi «Verifica motore».</li>
          <li>Le generazioni successive usano la versione nuova e il registro la riporta.</li>
          <li>I documenti già generati non cambiano.</li>
        </ol>
        <p className="mt-1.5 text-[11px] text-[#8a93a6]">
          Per aggiornare i testi non serve nessun deploy. Serve un deploy solo se cambiano i file del motore
          (render.js o generate.js).
        </p>
      </div>

      {/* Dati di prova */}
      <div className="mt-5 pt-4 border-t border-[#1A2B4A]/10">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[12px] font-semibold text-[#1A2B4A]">Elimina dati di prova</h3>
          <span className="text-[11px] text-[#8a93a6]">Tutto quello che contiene «ZZTEST», nel database e su Drive.</span>
          <button data-testid="dati-prova-elenca" onClick={elencaProva} disabled={pulisco}
            className="ml-auto inline-flex items-center gap-1.5 text-xs border border-[#1A2B4A]/25 px-3 py-1.5 rounded-md hover:border-[#C9A84C] disabled:opacity-50">
            {pulisco ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Elenca
          </button>
        </div>
        {prova && (
          <div data-testid="dati-prova-elenco" className="mt-3 rounded-md border border-[#9b2c2c]/25 bg-[#FDECEC] px-3 py-2.5 text-[11px] text-[#9b2c2c]">
            <p className="font-medium">
              {prova.clienti.length} clienti · {prova.pratiche.length} pratiche · {prova.registro.length} righe di registro ·
              {" "}{prova.tipi_pratica.length} tipi pratica · {prova.drive.length} voci su Drive
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {prova.clienti.map((c) => (
                <li key={c.id}>Cliente: {c.ragione_sociale || `${c.cognome} ${c.nome}`}{c.is_azienda ? " (impresa)" : ""}</li>
              ))}
              {prova.pratiche.map((p) => <li key={p.id}>Pratica: {p.titolo}</li>)}
              {prova.registro.map((r) => <li key={r.id}>Registro: {r.nome_file} · {r.stato}</li>)}
              {prova.tipi_pratica.map((t) => <li key={t.id}>Tipo pratica: {t.nome}</li>)}
              {prova.drive.map((d) => <li key={d.id}>Drive ({d.tipo}): {d.percorso}/{d.nome}</li>)}
            </ul>
            {prova.clienti.length + prova.pratiche.length + prova.registro.length + prova.drive.length === 0 ? (
              <p className="mt-2">Non c'è niente da eliminare.</p>
            ) : (
              <button data-testid="dati-prova-elimina" onClick={eliminaProva} disabled={pulisco}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#9b2c2c] text-white rounded-md disabled:opacity-50">
                {pulisco ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Conferma: cestina su Drive e cancella i record
              </button>
            )}
          </div>
        )}
      </div>

      {legacy && (
        legacy.tipi.length === 0 ? (
          <p data-testid="documenti-tipi-ok" className="mt-4 text-[12px] text-[#1F5C42]">
            Tutti i tipi pratica attivi hanno una riga nel catalogo template {legacy.versione_template}.
          </p>
        ) : (
          <div data-testid="documenti-tipi-fuori" className="mt-4 rounded-md border border-[#C9A84C]/50 bg-[#FDFAF1] px-3 py-2.5">
            <p className="text-[12px] font-medium text-[#7a5c12] flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              {legacy.tipi.length === 1
                ? "Un tipo pratica attivo non è nel catalogo template"
                : `${legacy.tipi.length} tipi pratica attivi non sono nel catalogo template`}
              <span className="text-[#8a93a6] font-normal">({legacy.versione_template})</span>
            </p>
            <p className="text-[11px] text-[#7a5c12] mt-1">
              Nessun abbinamento automatico: le pratiche vanno riassegnate a mano ai tipi nuovi. Su queste
              pratiche «Genera documento» mostra solo i documenti per il cliente.
            </p>
            <div className="mt-2 space-y-2">
              {legacy.tipi.map((t) => (
                <div key={t.id} data-testid={`tipo-legacy-${t.codice || t.id}`}
                  className="rounded-md bg-white/70 border border-[#C9A84C]/30 px-2.5 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12px] font-semibold text-[#1A2B4A]">{t.nome}</span>
                    <span className="text-[10px] text-[#8a93a6]">{t.codice || "senza codice"}</span>
                    <span data-testid={`tipo-legacy-conteggio-${t.codice || t.id}`}
                      className="text-[11px] text-[#7a5c12] inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {t.n_pratiche} {t.n_pratiche === 1 ? "pratica" : "pratiche"}
                    </span>
                    {t.non_selezionabile && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#8a93a6]/40 text-[#4a5568]">
                        non selezionabile per le pratiche nuove
                      </span>
                    )}
                    <button data-testid={`tipo-legacy-toggle-${t.codice || t.id}`} onClick={() => cambiaSelezionabile(t)}
                      className="ml-auto text-[11px] underline decoration-[#C9A84C]/60 text-[#1A2B4A] hover:text-[#C9A84C]">
                      {t.non_selezionabile ? "Rendi di nuovo selezionabile" : "Non più selezionabile"}
                    </button>
                  </div>
                  {t.pratiche.length > 0 && (
                    <ul className="mt-1 flex flex-col gap-y-0.5">
                      {t.pratiche.map((p) => (
                        <li key={p.pratica_id} className="text-[11px] text-[#4a5568] w-full">
                          <a href={`/pratiche/${p.pratica_id}`} className="underline decoration-[#C9A84C]/40 hover:text-[#C9A84C]">
                            {p.cliente}
                          </a>
                          <span className="text-[#8a93a6]">
                            {p.aperta_il ? ` · aperta il ${p.aperta_il.split("-").reverse().join("/")}` : ""}
                            {p.stato ? ` · ${p.stato}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}
